import CheckIcon from "@mui/icons-material/Check";
import { Box, ButtonBase, Typography } from "@mui/material";

import { useContactCenterTheme } from "./ContactCenterThemeContext";
import { ContactCenterTheme } from "./contactCenterThemes";

const BLOCK = 8;

const PREVIEW_MATRIX = [
  "hhhhhhhhaa",
  "bbbbbbbbbb",
  "iiibbbbbbb",
  "iiiibbbbbb",
  "bbbbboooob",
  "bbbbbboooo",
  "nnnnnnnnaa",
];

const previewColor = (theme: ContactCenterTheme, token: string): string => {
  switch (token) {
    case "h":
      return theme.swatch.header;
    case "a":
      return theme.swatch.accent;
    case "i":
      return theme.swatch.bubbleIn;
    case "o":
      return theme.swatch.bubbleOut;
    case "n":
      return theme.chat.inputBg;
    default:
      return theme.swatch.background;
  }
};

function ThemePreview({ theme }: { theme: ContactCenterTheme }) {
  const columns = PREVIEW_MATRIX[0].length;

  return (
    <Box
      aria-hidden
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, ${BLOCK}px)`,
        gridAutoRows: `${BLOCK}px`,
        borderRadius: "4px",
        overflow: "hidden",
        backgroundColor: theme.swatch.background,
        boxShadow: `inset 0 0 0 1px ${theme.shell.border}`,
      }}
    >
      {PREVIEW_MATRIX.flatMap((row, rowIndex) =>
        row.split("").map((token, columnIndex) => (
          <Box key={`${rowIndex}-${columnIndex}`} sx={{ backgroundColor: previewColor(theme, token) }} />
        )),
      )}
    </Box>
  );
}

export function ThemePicker() {
  const { themes, themeId, setThemeId, theme: activeTheme } = useContactCenterTheme();

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(118px, 1fr))",
        gap: 1.25,
      }}
      role="radiogroup"
      aria-label="Contact Center theme"
    >
      {themes.map((theme) => {
        const isSelected = theme.id === themeId;

        return (
          <ButtonBase
            key={theme.id}
            role="radio"
            aria-checked={isSelected}
            onClick={() => setThemeId(theme.id)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.75,
              p: 1,
              borderRadius: "10px",
              position: "relative",
              backgroundColor: theme.shell.bg,
              border: "2px solid",
              borderColor: isSelected ? activeTheme.shell.accent : theme.shell.border,
              transition: "border-color 0.18s ease, transform 0.18s ease",
              "&:hover": { transform: "translateY(-2px)" },
            }}
          >
            <ThemePreview theme={theme} />

            <Typography
              sx={{
                fontSize: 11,
                fontWeight: isSelected ? 800 : 600,
                color: theme.shell.textPrimary,
                lineHeight: 1.2,
              }}
            >
              {theme.name}
            </Typography>

            {isSelected && (
              <Box
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  backgroundColor: activeTheme.shell.accent,
                  color: "#fff",
                }}
              >
                <CheckIcon sx={{ fontSize: 11 }} />
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
